// Geometric mask cleanup pipeline for a clean, stroke-free sticker cutout:
//   1. threshold the (already guided-filter-refined) alpha to binary
//   2. morphological opening (erode -> dilate) with a disk structuring
//      element, sized relative to image resolution - knocks off small
//      pixel-level noise/spikes
//   3. drop every connected component except the largest (tiny disconnected
//      specks left over from step 2)
//   4. trace the largest component's boundary, compare it against its own
//      convex hull, and pull in points that deviate sharply (protrusions
//      that survived the opening - e.g. a stray thin spike)
//   5. rasterize the corrected polygon, then either blur+rethreshold or
//      (not implemented here, see comment) polygon-simplify to smooth it
//   6. feather the final edge by a couple pixels so it composites without
//      a hard stroke
//
// All size/distance parameters scale with image resolution rather than
// being fixed pixel counts, per spec.

export interface CleanupOptions {
    /** structuring-element / feather radii as a fraction of the shorter image dimension */
    openingFraction?: number; // default 0.005 (0.5%)
    openingFloorPx?: number; // default 3
    smoothBlurFraction?: number; // default 0.004
    smoothBlurFloorPx?: number; // default 2
    featherFraction?: number; // default 0.0025
    featherFloorPx?: number; // default 2
    /** how far (in units of the opening radius) a contour point must bulge past a locally-smoothed hull-distance baseline to count as a protrusion */
    protrusionThresholdMultiplier?: number; // default 2.5
}

const DEFAULTS: Required<CleanupOptions> = {
    openingFraction: 0.005,
    openingFloorPx: 3,
    smoothBlurFraction: 0.004,
    smoothBlurFloorPx: 2,
    featherFraction: 0.0025,
    featherFloorPx: 2,
    protrusionThresholdMultiplier: 2.5,
};

export function scaledRadius(shortDim: number, fraction: number, floorPx: number): number {
    return Math.max(floorPx, Math.round(shortDim * fraction));
}

// ---------- 1D sliding min/max (monotonic deque, O(n) regardless of window width) ----------

function slidingExtreme(values: Uint8Array, width: number, isMin: boolean): Uint8Array {
    const n = values.length;
    const out = new Uint8Array(n);
    const half = Math.floor(width / 2);
    // deque holds indices, values monotonic (increasing for min, decreasing for max)
    const deque: number[] = [];
    let head = 0;
    for (let i = -half; i < n + half; i++) {
        const idx = Math.min(n - 1, Math.max(0, i));
        const v = values[idx];
        while (deque.length > head && (isMin ? values[deque[deque.length - 1]] >= v : values[deque[deque.length - 1]] <= v)) {
            deque.pop();
        }
        deque.push(idx);
        const outIdx = i + half;
        // drop indices that fell out of the window on the left
        while (deque[head] < outIdx - width + 1) head++;
        if (outIdx >= 0 && outIdx < n) out[outIdx] = values[deque[head]];
    }
    return out;
}

// ---------- disk erosion/dilation via per-row-offset horizontal extreme, decomposed circle ----------

export function diskMorph(mask: Uint8Array, w: number, h: number, r: number, isErode: boolean): Uint8Array {
    if (r <= 0) return mask.slice();
    // out(x,y) = extreme over dy in [-r,r] of [horizontal-extreme(row y+dy, width 2*halfWidth(dy)+1)](x)
    const out = new Uint8Array(w * h);
    out.fill(isErode ? 255 : 0);

    // precompute per-dy half-widths (disk cross-section)
    const offsets: { dy: number; halfWidth: number }[] = [];
    for (let dy = -r; dy <= r; dy++) {
        const halfWidth = Math.floor(Math.sqrt(Math.max(0, r * r - dy * dy)));
        offsets.push({ dy, halfWidth });
    }

    const rowBuf = new Uint8Array(w);
    for (const { dy, halfWidth } of offsets) {
        const width = 2 * halfWidth + 1;
        for (let y = 0; y < h; y++) {
            const srcY = Math.min(h - 1, Math.max(0, y + dy));
            rowBuf.set(mask.subarray(srcY * w, srcY * w + w));
            const filtered = slidingExtreme(rowBuf, width, isErode);
            const outRow = y * w;
            if (isErode) {
                for (let x = 0; x < w; x++) out[outRow + x] = Math.min(out[outRow + x], filtered[x]);
            } else {
                for (let x = 0; x < w; x++) out[outRow + x] = Math.max(out[outRow + x], filtered[x]);
            }
        }
    }
    return out;
}

export function morphologicalOpen(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
    const eroded = diskMorph(mask, w, h, r, true);
    return diskMorph(eroded, w, h, r, false);
}

export function morphologicalClose(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
    const dilated = diskMorph(mask, w, h, r, false);
    return diskMorph(dilated, w, h, r, true);
}

// ---------- connected components: keep only the largest ----------

export function keepLargestComponent(mask: Uint8Array, w: number, h: number): Uint8Array {
    const labels = new Int32Array(w * h).fill(-1);
    let bestLabel = -1;
    let bestSize = 0;
    let nextLabel = 0;
    const stack: number[] = [];

    for (let start = 0; start < w * h; start++) {
        if (mask[start] === 0 || labels[start] !== -1) continue;
        const label = nextLabel++;
        let size = 0;
        stack.length = 0;
        stack.push(start);
        labels[start] = label;
        while (stack.length) {
            const i = stack.pop()!;
            size++;
            const x = i % w;
            const y = (i / w) | 0;
            const neighbors = [
                x > 0 ? i - 1 : -1,
                x < w - 1 ? i + 1 : -1,
                y > 0 ? i - w : -1,
                y < h - 1 ? i + w : -1,
            ];
            for (const n of neighbors) {
                if (n >= 0 && mask[n] !== 0 && labels[n] === -1) {
                    labels[n] = label;
                    stack.push(n);
                }
            }
        }
        if (size > bestSize) {
            bestSize = size;
            bestLabel = label;
        }
    }

    const out = new Uint8Array(w * h);
    if (bestLabel === -1) return out;
    for (let i = 0; i < w * h; i++) out[i] = labels[i] === bestLabel ? 255 : 0;
    return out;
}

// ---------- Moore-neighbor boundary tracing ----------

export interface Point {
    x: number;
    y: number;
}

export function traceBoundary(mask: Uint8Array, w: number, h: number): Point[] {
    const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : mask[y * w + x]);

    let start: Point | null = null;
    outer: for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            if (at(x, y) !== 0) {
                start = { x, y };
                break outer;
            }
        }
    }
    if (!start) return [];

    // 8-connected clockwise neighbor offsets, starting "west" of the current backtrack direction
    const dirs = [
        [-1, 0], [-1, -1], [0, -1], [1, -1],
        [1, 0], [1, 1], [0, 1], [-1, 1],
    ];

    const boundary: Point[] = [start];
    let current = start;
    let backtrackDir = 0; // came from "west" initially
    const maxSteps = w * h * 4;
    let steps = 0;

    do {
        let dir = (backtrackDir + 1) % 8;
        let found: Point | null = null;
        let foundDir = dir;
        for (let k = 0; k < 8; k++) {
            const d = dirs[dir];
            const nx = current.x + d[0];
            const ny = current.y + d[1];
            if (at(nx, ny) !== 0) {
                found = { x: nx, y: ny };
                foundDir = dir;
                break;
            }
            dir = (dir + 1) % 8;
        }
        if (!found) break;
        backtrackDir = (foundDir + 4) % 8; // opposite direction, for next search start
        current = found;
        boundary.push(current);
        steps++;
    } while (!(current.x === start.x && current.y === start.y) && steps < maxSteps);

    return boundary;
}

// ---------- convex hull (Andrew's monotone chain) ----------

function cross(o: Point, a: Point, b: Point): number {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

export function convexHull(points: Point[]): Point[] {
    const pts = [...new Map(points.map((p) => [`${p.x},${p.y}`, p])).values()].sort(
        (a, b) => a.x - b.x || a.y - b.y
    );
    if (pts.length < 3) return pts;

    const lower: Point[] = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    }
    const upper: Point[] = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

// distance from point p to the nearest point on segment ab
function pointToSegmentDist(p: Point, a: Point, b: Point): number {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    let t = len2 === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
    t = Math.max(0, Math.min(1, t));
    const projX = a.x + t * abx;
    const projY = a.y + t * aby;
    return Math.hypot(p.x - projX, p.y - projY);
}

function distanceToHull(p: Point, hull: Point[]): number {
    let best = Infinity;
    for (let i = 0; i < hull.length; i++) {
        const a = hull[i];
        const b = hull[(i + 1) % hull.length];
        best = Math.min(best, pointToSegmentDist(p, a, b));
    }
    return best;
}

/**
 * Removes sharp protrusions: contour points whose distance to the convex
 * hull spikes well past a locally-smoothed baseline get pulled back to
 * that baseline (interpolated from their non-spiking neighbors).
 */
export function removeProtrusions(contour: Point[], hull: Point[], thresholdPx: number): Point[] {
    if (contour.length < 5 || hull.length < 3) return contour;

    const dists = contour.map((p) => distanceToHull(p, hull));

    // local baseline = median of a window around each point (cheap moving median via sort of a small window)
    const windowRadius = Math.max(3, Math.round(contour.length * 0.01));
    const baseline = dists.map((_, i) => {
        const window: number[] = [];
        for (let k = -windowRadius; k <= windowRadius; k++) {
            window.push(dists[(i + k + dists.length) % dists.length]);
        }
        window.sort((a, b) => a - b);
        return window[Math.floor(window.length / 2)];
    });

    const isSpike = dists.map((d, i) => d - baseline[i] > thresholdPx);

    const cleaned: Point[] = contour.map((p, i) => {
        if (!isSpike[i]) return p;
        // pull spike points toward the average of their nearest non-spike neighbors
        let before = i;
        for (let k = 1; k < contour.length; k++) {
            const idx = (i - k + contour.length) % contour.length;
            if (!isSpike[idx]) { before = idx; break; }
        }
        let after = i;
        for (let k = 1; k < contour.length; k++) {
            const idx = (i + k) % contour.length;
            if (!isSpike[idx]) { after = idx; break; }
        }
        return {
            x: (contour[before].x + contour[after].x) / 2,
            y: (contour[before].y + contour[after].y) / 2,
        };
    });

    return cleaned;
}

// ---------- main pipeline ----------

export interface CleanupResult {
    /** clean, smoothed near-binary [0,1] mask (steps 1-6) - not yet feathered */
    mask: Float32Array;
    /** resolution-scaled feather radius (px) the caller should use for step 7 */
    featherRadiusPx: number;
}

/**
 * @param alpha continuous [0,1] alpha (post guided-filter), length w*h
 * @returns the geometrically-cleaned mask (steps 1-6) plus the feather
 * radius to use for step 7, which the caller performs (see note above)
 */
export function cleanupMask(alpha: Float32Array, w: number, h: number, opts: CleanupOptions = {}): CleanupResult {
    const o = { ...DEFAULTS, ...opts };
    const shortDim = Math.min(w, h);
    const openingR = scaledRadius(shortDim, o.openingFraction, o.openingFloorPx);
    const smoothR = scaledRadius(shortDim, o.smoothBlurFraction, o.smoothBlurFloorPx);
    const featherR = scaledRadius(shortDim, o.featherFraction, o.featherFloorPx);

    // 1. threshold
    let mask = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) mask[i] = alpha[i] >= 0.5 ? 255 : 0;

    // 2. morphological opening (disk structuring element)
    mask = morphologicalOpen(mask, w, h, openingR);

    // 3. drop everything but the largest connected component
    mask = keepLargestComponent(mask, w, h);

    // 4. trace boundary, compare to convex hull, pull in protrusions
    const contour = traceBoundary(mask, w, h);
    let polygon = contour;
    if (contour.length >= 5) {
        const hull = convexHull(contour);
        const protrusionThresholdPx = openingR * o.protrusionThresholdMultiplier;
        polygon = removeProtrusions(contour, hull, protrusionThresholdPx);
    }

    // rasterize the corrected polygon via canvas fill (robust, avoids a
    // hand-rolled scanline fill)
    const rasterCanvas = document.createElement("canvas");
    rasterCanvas.width = w;
    rasterCanvas.height = h;
    const rctx = rasterCanvas.getContext("2d")!;
    if (polygon.length >= 3) {
        rctx.fillStyle = "white";
        rctx.beginPath();
        rctx.moveTo(polygon[0].x, polygon[0].y);
        for (let i = 1; i < polygon.length; i++) rctx.lineTo(polygon[i].x, polygon[i].y);
        rctx.closePath();
        rctx.fill();
    }

    // 6. smooth: light blur + re-threshold at 0.5 (native canvas blur, then
    // pixel-wise threshold back to a hard mask)
    const smoothCanvas = document.createElement("canvas");
    smoothCanvas.width = w;
    smoothCanvas.height = h;
    const sctx = smoothCanvas.getContext("2d")!;
    sctx.filter = `blur(${smoothR}px)`;
    sctx.drawImage(rasterCanvas, 0, 0);
    const smoothedData = sctx.getImageData(0, 0, w, h);

    const rethresholded = document.createElement("canvas");
    rethresholded.width = w;
    rethresholded.height = h;
    const tctx = rethresholded.getContext("2d")!;
    const tData = tctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
        const v = smoothedData.data[i * 4] >= 128 ? 255 : 0; // alpha channel via white fill's R/A both work; using R
        tData.data[i * 4] = v;
        tData.data[i * 4 + 1] = v;
        tData.data[i * 4 + 2] = v;
        tData.data[i * 4 + 3] = 255;
    }
    tctx.putImageData(tData, 0, 0);

    // Step 7 (feathering) is deliberately NOT done here with a plain blur:
    // a content-blind spatial blur smears in whatever color happens to be
    // near the edge (grass, sky, ...) rather than respecting the real
    // photo's edges, undoing exactly what the guided filter upstream is
    // for. Instead we return the clean hard-ish mask plus the resolution-
    // scaled feather radius, and the caller (which already has the guide
    // image) feathers it with a second guided-filter pass - same "snap to
    // real image content" behavior as the first refinement, just at a
    // tighter radius sized for feathering rather than upscaling.
    const cleanMask = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) cleanMask[i] = tData.data[i * 4] / 255;
    return { mask: cleanMask, featherRadiusPx: featherR };
}

// ---------- extra building blocks for alternate pipeline variants ----------

export function thresholdAlpha(alpha: Float32Array, w: number, h: number, t = 0.5): Uint8Array {
    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = alpha[i] >= t ? 255 : 0;
    return out;
}

/** Rasterizes a filled polygon (canvas fill - robust, antialiased) as a 0/255-ish mask. */
export function rasterizePolygon(polygon: Point[], w: number, h: number): Uint8Array {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    if (polygon.length >= 3) {
        ctx.fillStyle = "white";
        ctx.beginPath();
        ctx.moveTo(polygon[0].x, polygon[0].y);
        for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i].x, polygon[i].y);
        ctx.closePath();
        ctx.fill();
    }
    const data = ctx.getImageData(0, 0, w, h).data;
    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = data[i * 4];
    return out;
}

/** Gaussian-ish blur via canvas's native CSS filter (content-blind - fine for
 * blurring a MASK, not a real photo edge, since there's no color info to lose). */
export function blurMask(mask: Uint8Array, w: number, h: number, radiusPx: number): Uint8Array {
    const src = document.createElement("canvas");
    src.width = w;
    src.height = h;
    const sctx = src.getContext("2d")!;
    const srcData = sctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
        srcData.data[i * 4] = mask[i];
        srcData.data[i * 4 + 1] = mask[i];
        srcData.data[i * 4 + 2] = mask[i];
        srcData.data[i * 4 + 3] = 255;
    }
    sctx.putImageData(srcData, 0, 0);

    const dst = document.createElement("canvas");
    dst.width = w;
    dst.height = h;
    const dctx = dst.getContext("2d")!;
    dctx.filter = `blur(${radiusPx}px)`;
    dctx.drawImage(src, 0, 0);
    const dstData = dctx.getImageData(0, 0, w, h).data;

    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = dstData[i * 4];
    return out;
}

export function rethreshold(mask: Uint8Array, t = 128): Uint8Array {
    const out = new Uint8Array(mask.length);
    for (let i = 0; i < mask.length; i++) out[i] = mask[i] >= t ? 255 : 0;
    return out;
}

export function maskToAlpha(mask: Uint8Array): Float32Array {
    const out = new Float32Array(mask.length);
    for (let i = 0; i < mask.length; i++) out[i] = mask[i] / 255;
    return out;
}

/**
 * Fits an axis-aligned ellipse to the foreground pixels of `mask` (centered
 * on their centroid, sized to their extent plus `marginPx`) and rasterizes
 * it as a filled mask - a "clean circle/oval around the object" that
 * ignores the subject's actual silhouette entirely.
 */
export function fitBoundingEllipseMask(mask: Uint8Array, w: number, h: number, marginPx: number): Uint8Array {
    let minX = w, maxX = -1, minY = h, maxY = -1;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            if (mask[y * w + x] !== 0) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }
    const out = new Uint8Array(w * h);
    if (maxX < 0) return out; // empty mask

    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const rx = (maxX - minX) / 2 + marginPx;
    const ry = (maxY - minY) / 2 + marginPx;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
    ctx.fill();
    const data = ctx.getImageData(0, 0, w, h).data;
    for (let i = 0; i < w * h; i++) out[i] = data[i * 4];
    return out;
}

/**
 * Removes stray disconnected noise (a small blob the model spuriously
 * predicted somewhere else in the frame) WITHOUT touching the shape of the
 * real subject's edge at all. Unlike cleanupMask, this never re-thresholds,
 * smooths, or geometrically corrects the kept region - it only decides,
 * per pixel, "is this part of the largest connected blob or not," and
 * zeroes out anything that isn't. The kept pixels retain their exact
 * original (soft, guided-filter-refined) alpha values.
 *
 * A tiny opening is used ONLY to decide connectivity (so a single-pixel
 * noise speck doesn't get counted as "connected" via a diagonal hair-thin
 * bridge) - it does not affect which alpha values are kept.
 */
export function removeStrayNoise(alpha: Float32Array, w: number, h: number, openingFloorPx = 2): Float32Array {
    const shortDim = Math.min(w, h);
    const r = scaledRadius(shortDim, 0.002, openingFloorPx);

    let mask = thresholdAlpha(alpha, w, h);
    mask = morphologicalOpen(mask, w, h, r);
    const largest = keepLargestComponent(mask, w, h);

    // Dilate the keep-region back out by the same radius so we don't clip
    // real (soft) edge pixels that the opening's erosion pass ate into -
    // this mask is only used to decide keep/discard, not as the alpha itself.
    const keepRegion = diskMorph(largest, w, h, r, false);

    const out = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = keepRegion[i] !== 0 ? alpha[i] : 0;
    return out;
}
